import type { NextRouter } from "next/router"
import type { ReactNode } from "react"
import React from "react"

import { BookingSourceContext } from "@/lib/booking_source/context"
import { Loading } from "@/lib/loading"
import { ReactPerformance } from "@/lib/react_performance"

type Props = {
  children: ReactNode
  loading: Loading.ContextData
  router: NextRouter
}

type State = {
  bookingSource?: string
  loadingConfig: Loading.Config
  update: boolean
}

export class BookingSourceContainerComponent extends React.Component<
  Props,
  State
> {
  private extractFromRouter = () => {
    return this.props.router.query.refer?.toString()
  }

  public state: State = {
    bookingSource: this.props.loading.processes.routing.loading
      ? this.extractFromRouter()
      : undefined,
    loadingConfig: {
      dependencies: [`routing`],
      processId: `bookingSource`,
    },
    update: false,
  }

  public componentDidMount = () => {
    this.componentMaybeReady()
  }

  public componentDidUpdate = () => {
    this.componentMaybeReady()
  }

  private componentMaybeReady = Loading.curryOnMaybeReady(this, {
    onDependenciesResolved: () => {
      this.setBookingSource(this.extractFromRouter())
    },
  })

  public render = (): ReactNode => {
    return (
      <BookingSourceContext.Provider
        value={{
          bookingSource: this.state.bookingSource,
          setBookingSource: this.setBookingSource,
        }}
      >
        {this.props.children}
      </BookingSourceContext.Provider>
    )
  }

  private setBookingSource = (bookingSource?: string) => {
    this.updateState({ bookingSource })
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    Props,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps, nextState }) => {
      if (Loading.shouldComponentReady(nextProps, nextState)) {
        return { update: true }
      }

      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  private updateState = ReactPerformance.curryUpdateManagedState(this)
}
